import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { masterApi, apiError, formatPhone } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Switch } from "@/components/ui/switch";
import { Send, Link2, CheckCircle2, RefreshCw, AlertCircle, ChevronLeft, ChevronRight, Check, QrCode, Smartphone, Power, Loader2, Trash2, Settings, Save, ShieldCheck, Plug, ChevronDown } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

const TIPOS_LABEL = {
  otp: "OTP", saudade: "Saudade", pesquisa: "Pesquisa",
  agradecimento: "Agradecimento", indicacao: "Indicação", campanha: "Campanha",
  geral: "Geral", teste: "Teste",
};

// Máscara progressiva de telefone BR: (DD) NNNNN-NNNN (celular) / (DD) NNNN-NNNN (fixo).
function maskTel(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// ===================== API oficial do WhatsApp (Meta, via CdxZap) =====================
// Contrato: GET /master/whatsapp/oficial/status · PUT /oficial/config · POST /oficial/testar-conexao
//           POST /oficial/templates/sincronizar · GET /oficial/templates (cache local).
// A chave do CdxZap fica só no .env do servidor; esta tela nunca a vê.

const PROVEDOR_OFICIAL = "cdxzap_oficial";

// Falha de envio: a tela mantém a frase fixa de sempre e mostra o detalhe do provedor só como
// uma linha extra, curta (o backend já tira segredos; aqui só resume e corta).
const DETALHE_MAX = 160;
function resumoDetalhe(d) {
  const s = String(d ?? "").replace(/\s+/g, " ").trim();
  if (!s) return null;
  return s.length > DETALHE_MAX ? `${s.slice(0, DETALHE_MAX - 3).trimEnd()}...` : s;
}
function detalhesDe(d) {
  const r = resumoDetalhe(d);
  return r ? [r] : undefined;
}

const TPL_STATUS = {
  APPROVED: { label: "Aprovado", cls: "chip-green" },
  PENDING: { label: "Em análise", cls: "chip-yellow" },
  IN_APPEAL: { label: "Em recurso", cls: "chip-yellow" },
  REJECTED: { label: "Reprovado", cls: "chip-red" },
  PAUSED: { label: "Pausado", cls: "chip-orange" },
  DISABLED: { label: "Desativado", cls: "chip-red" },
  AUSENTE: { label: "Ausente", cls: "chip-gray" },
};

const QUALIDADE = {
  GREEN: { label: "Alta", accent: "emerald" },
  YELLOW: { label: "Média", accent: null },
  RED: { label: "Baixa", accent: "rose" },
};

const AMBIENTE_LABEL = { teste: "Teste", producao: "Produção" };

function tplStatusLabel(s) {
  return (TPL_STATUS[s] || {}).label || s || "—";
}

function qualidadeInfo(q) {
  if (!q) return { label: "—", accent: null };
  return QUALIDADE[String(q).toUpperCase()] || { label: q, accent: null };
}

function fmtDataHora(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function TplPill({ status }) {
  const st = TPL_STATUS[status] || { label: status || "—", cls: "chip-gray" };
  return <span className={`chip ${st.cls} text-[10px] py-0.5 uppercase tracking-wider whitespace-nowrap`}>{st.label}</span>;
}

export default function MasterWhatsApp() {
  const [sp] = useSearchParams();
  // permite abrir direto numa aba via ?tab= (ex.: link "Conectar" das Integrações → ?tab=status)
  const tabInicial = ["envio", "status", "historico"].includes(sp.get("tab")) ? sp.get("tab") : "envio";
  const [tab, setTab] = useState(tabInicial); // envio | status (Conexão) | historico

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-whatsapp">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">WhatsApp · Mensageria</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Mensageria</h1>
      </div>

      <div className="flex gap-1 p-1 bg-white/80 border border-neutral-200/70 rounded-full w-fit">
        {[
          ["envio", "Envio único"],
          ["status", "Conexão"],
          ["historico", "Histórico"],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`wa-tab-${k}`}
                  className={`px-4 h-8 rounded-full text-[12px] font-medium transition-colors ${
                    tab === k ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
                  }`}>{lbl}</button>
        ))}
      </div>

      {tab === "envio" && <EnvioUnico />}
      {tab === "status" && <Conexao />}
      {tab === "historico" && <Historico />}
    </div>
  );
}

function Conexao() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState(null);
  const [awaiting, setAwaiting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [limpando, setLimpando] = useState(false);
  const [tel, setTel] = useState("");
  const [sending, setSending] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cfgForm, setCfgForm] = useState({ instance_id: "", token: "", client_token: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  // API oficial (Meta via CdxZap). 404 = backend ainda sem as rotas /oficial: o bloco some e o legado segue igual.
  const [oficial, setOficial] = useState(null);
  const [oficialErro, setOficialErro] = useState(null);
  const [oficialSuportado, setOficialSuportado] = useState(true);
  const [oficialCarregando, setOficialCarregando] = useState(false);
  const oficialAtivo = oficial?.provedor_ativo === PROVEDOR_OFICIAL;

  const loadOficial = useCallback(async () => {
    setOficialCarregando(true);
    try {
      const { data } = await masterApi.get("/master/whatsapp/oficial/status");
      setOficial(data); setOficialErro(null); setOficialSuportado(true);
    } catch (e) {
      if (e?.response?.status === 404) setOficialSuportado(false);
      else setOficialErro(apiError(e));
    } finally { setOficialCarregando(false); }
  }, []);
  useEffect(() => { loadOficial(); }, [loadOficial]);

  // Com a API oficial em uso, QR Code / conectar / desconectar / limpar do legado ficam fora (a API responde 400).
  useEffect(() => {
    if (oficialAtivo) { setQr(null); setAwaiting(false); }
  }, [oficialAtivo]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await masterApi.get("/master/whatsapp/status");
      setStatus(data);
      if (data.connected) { setQr(null); setAwaiting(false); }
    } catch (e) { modal.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadCfg = async () => {
    try {
      const { data } = await masterApi.get("/master/whatsapp/config");
      setCfg(data);
      setCfgForm({ instance_id: data.instance_id || "", token: "", client_token: "" });
    } catch { /* silencioso */ }
  };
  useEffect(() => { loadCfg(); }, []);

  const saveCfg = async () => {
    if (!cfgForm.instance_id.trim()) { modal.error("Informe o Instance ID da Z-API"); return; }
    setSavingCfg(true);
    try {
      await masterApi.post("/master/whatsapp/config", cfgForm);
      modal.success("Configuração Z-API salva");
      await loadCfg(); await load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSavingCfg(false); }
  };

  // Polling enquanto aguarda o pareamento (QR aberto)
  useEffect(() => {
    if (!awaiting || oficialAtivo) return;
    const tick = async () => {
      try {
        const { data } = await masterApi.get("/master/whatsapp/status");
        setStatus(data);
        if (data.connected) { setQr(null); setAwaiting(false); modal.success("WhatsApp conectado!"); return; }
        const r = await masterApi.get("/master/whatsapp/qrcode");
        if (r.data?.qr_code) setQr(r.data.qr_code);
      } catch { /* silencioso durante polling */ }
    };
    const t = setInterval(tick, 3500);
    return () => clearInterval(t);
  }, [awaiting, oficialAtivo]);

  const conectar = async () => {
    setConnecting(true); setQr(null);
    try {
      const { data } = await masterApi.post("/master/whatsapp/conectar");
      if (data.ok === false) { modal.error(data.detail || "Falha ao conectar"); }
      else { if (data.qr_code) setQr(data.qr_code); setAwaiting(true); }  // QR aparece inline no card (sem modal)
      load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setConnecting(false); }
  };

  const desconectar = async () => {
    try {
      await masterApi.post("/master/whatsapp/desconectar");
      setQr(null); setAwaiting(false); modal.success("Sessão desconectada"); load();
    } catch (e) { modal.error(apiError(e)); }
  };

  const limpar = async () => {
    const ok = await modal.confirm({
      title: "Limpar conexão",
      message: "A conexão do WhatsApp será reiniciada — isso limpa estados antigos que podem travar o QR Code.",
      details: ["Depois você gera um novo QR Code para reconectar o WhatsApp."],
      confirmText: "Limpar tudo",
      cancelText: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    setLimpando(true);
    try {
      const { data } = await masterApi.post("/master/whatsapp/limpar");
      const n = data.removidas?.length || 0;
      if (data.ok) modal.success(`Conexões limpas — ${n} sessão(ões) removida(s). Gere um novo QR Code.`);
      else modal.error(data.detail || "Falha ao limpar conexões");
      setQr(null); setAwaiting(false); load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setLimpando(false); }
  };

  const testar = async () => {
    if (!tel.trim()) { modal.error("Informe um telefone (com DDD)"); return; }
    setSending(true);
    try {
      const { data } = await masterApi.post("/master/whatsapp/teste", { telefone: tel });
      if (data.ok && data.canal_efetivo === "template") {
        modal.success({
          title: "Mensagem de teste enviada!",
          message: "Fora da janela de 24 h a API oficial só aceita template aprovado. Foi enviado o template de confirmação de cadastro.",
          details: detalhesDe(data.detalhe),
        });
      } else if (data.ok) modal.success("Mensagem de teste enviada!");
      else {
        modal.error({
          message: oficialAtivo
            ? "Falha no envio pela API oficial."
            : "Falha no envio. Verifique se o WhatsApp está conectado.",
          details: detalhesDe(data.detalhe),
        });
      }
    } catch (e) { modal.error(apiError(e)); }
    finally { setSending(false); }
  };

  const blocoOficial = oficialSuportado ? (
    <ApiOficial key="api-oficial" oficial={oficial} erro={oficialErro} carregando={oficialCarregando}
                onRecarregar={loadOficial} onAtualizado={setOficial} onProvedorAlterado={load} />
  ) : null;

  if (!status) {
    return (
      <div className="space-y-3" data-testid="wa-conexao-section">
        {blocoOficial}
        <div className="card-soft rounded-xl p-5 text-sm text-neutral-500">Carregando status...</div>
      </div>
    );
  }

  const ok = status.connected;
  const naoConfig = status.status === "nao_configurado";
  const PROV = { zapi: "Z-API", mock: "Mock", cdxzap_oficial: "API oficial (Meta)" }[status.modo] || "WhatsApp";

  return (
    <div className="space-y-3" data-testid="wa-conexao-section">
      {/* API oficial do WhatsApp (Meta via CdxZap) */}
      {blocoOficial}

      {/* Configuração Z-API (parametrizável pela UI) */}
      <div className="card-soft rounded-2xl p-5" data-testid="wa-config-card">
        <div className="flex items-center gap-2 mb-1">
          <Settings size={18} className="text-[color:var(--brand-blue)]" />
          <div className="font-serif-display text-lg">Configuração Z-API</div>
        </div>
        <p className="text-[12px] text-neutral-500 mb-3">
          Credenciais da sua instância no painel <b>app.z-api.io</b>. Ficam salvas no sistema — sem mexer no servidor.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Instance ID</label>
            <input data-testid="zapi-instance" value={cfgForm.instance_id}
                   onChange={(e) => setCfgForm({ ...cfgForm, instance_id: e.target.value })}
                   placeholder="3E…" className={inputCls + " mt-1"} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Token</label>
            <input data-testid="zapi-token" type="password" value={cfgForm.token}
                   onChange={(e) => setCfgForm({ ...cfgForm, token: e.target.value })}
                   placeholder={cfg?.token_set ? "•••••• (salvo — deixe em branco p/ manter)" : "token da instância"}
                   className={inputCls + " mt-1"} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Client-Token <span className="normal-case text-neutral-400">(opcional)</span></label>
            <input data-testid="zapi-client" type="password" value={cfgForm.client_token}
                   onChange={(e) => setCfgForm({ ...cfgForm, client_token: e.target.value })}
                   placeholder={cfg?.client_token_set ? "•••• (salvo)" : "Account Security Token"}
                   className={inputCls + " mt-1"} />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-[11px] text-neutral-400">
            {cfg?.token_set ? "Token configurado ✓" : "Token ainda não configurado"}
          </span>
          <button onClick={saveCfg} disabled={savingCfg} data-testid="zapi-save" className="btn-action btn-salvar">
            {savingCfg ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {savingCfg ? "Salvando..." : "Salvar configuração"}
          </button>
        </div>
      </div>

      {/* Status da conexão */}
      <div className={`card-soft rounded-2xl p-5 border-l-4 ${ok ? "border-emerald-500" : "border-rose-500"}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="chip text-[10px] py-0.5 mb-1.5"
                 style={{ background: ok ? "rgba(16,185,129,0.18)" : "rgba(244,63,94,0.18)",
                          color: ok ? "#047857" : "#9F1239" }}>
              <span className="pulse-dot" /> {ok ? "Conectado" : "Desconectado"}
            </div>
            <div className="font-serif-display text-xl">
              {ok ? "WhatsApp ativo e pronto" : "WhatsApp não está conectado"}
            </div>
            <div className="text-[12px] text-neutral-600 mt-1">
              Provedor: <b>{PROV}</b>{status.numero ? <> · Número <b>{status.numero}</b></> : null} · {status.detail}
            </div>
            {oficialAtivo && (
              <div className="text-[11px] text-neutral-500 mt-1" data-testid="wa-legado-aviso-oficial">
                As mensagens estão saindo pela API oficial. QR Code, conectar, desconectar e limpar do WhatsApp não oficial ficam desativados.
              </div>
            )}
          </div>
          <div className="flex gap-1.5">
            <button onClick={load} disabled={loading} data-testid="wa-status-reload"
                    className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Atualizar
            </button>
            {ok && !oficialAtivo && (
              <button onClick={desconectar} data-testid="wa-desconectar"
                      className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 text-rose-700">
                <Power size={13} /> Desconectar
              </button>
            )}
            {!naoConfig && !oficialAtivo && (
              <button onClick={limpar} disabled={limpando} data-testid="wa-limpar"
                      title="Reinicia a conexão do WhatsApp (corrige QR que aparece e some)"
                      className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 text-amber-700">
                <Trash2 size={13} className={limpando ? "animate-pulse" : ""} /> {limpando ? "Limpando..." : "Limpar conexões"}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          <Mini label="Provedor" value={PROV} />
          <Mini label="Conectado" value={status.connected ? "Sim" : "Não"} accent={status.connected ? "emerald" : null} />
          <Mini label="Celular pareado" value={status.smartphone_connected ? "Sim" : "Não"} />
          <Mini label="Configurado" value={status.configured ? "Sim" : "Não"} accent={status.configured ? null : "rose"} />
        </div>

        <div className="grid grid-cols-3 gap-2 mt-2">
          <Mini label="Envios 24h" value={String(status.envios_24h?.total ?? 0)} />
          <Mini label="Sucesso" value={String(status.envios_24h?.sucesso ?? 0)} accent="emerald" />
          <Mini label="Falha" value={String(status.envios_24h?.falha ?? 0)} accent={status.envios_24h?.falha ? "rose" : null} />
        </div>
      </div>

      {/* Token ausente */}
      {naoConfig && !oficialAtivo && (
        <div className="card-soft rounded-xl p-4 flex items-start gap-3 bg-amber-50 border border-amber-200">
          <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={16} />
          <div className="text-[12px] text-amber-900 leading-relaxed">
            <b>{PROV} não configurado.</b> Defina as credenciais do provedor no servidor para habilitar a geração do QR Code e os envios.
          </div>
        </div>
      )}

      {/* QR Code / Conectar */}
      {!ok && !naoConfig && !oficialAtivo && (
        <div className="card-soft rounded-2xl p-5" data-testid="wa-qr-card">
          <div className="flex items-center gap-2 mb-3">
            <QrCode size={18} className="text-[color:var(--brand-blue)]" />
            <div className="font-serif-display text-lg">Conectar WhatsApp</div>
          </div>

          {qr ? (
            <div className="flex flex-col md:flex-row items-center gap-5">
              <div className="bg-white rounded-2xl border border-[var(--line)] p-3 shadow-sm shrink-0">
                <img src={qr} alt="QR Code do WhatsApp" data-testid="wa-qr-img"
                     className="w-[230px] h-[230px] object-contain" />
              </div>
              <div className="text-[13px] text-neutral-600 leading-relaxed space-y-2">
                <div className="flex items-center gap-2 text-[color:var(--brand-blue)] font-medium">
                  <Loader2 size={14} className="animate-spin" /> Aguardando leitura...
                </div>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Abra o <b>WhatsApp</b> no celular do posto</li>
                  <li>Toque em <b>Aparelhos conectados</b></li>
                  <li>Toque em <b>Conectar aparelho</b></li>
                  <li>Aponte a câmera para este QR Code</li>
                </ol>
                <button onClick={conectar} disabled={connecting} data-testid="wa-qr-refresh"
                        className="btn-ghost h-9 px-3 text-[12px] font-medium inline-flex items-center gap-1.5 mt-1">
                  <RefreshCw size={13} className={connecting ? "animate-spin" : ""} /> Gerar novo QR
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start gap-3">
              <p className="text-[13px] text-neutral-600">
                {awaiting
                  ? "Gerando o QR Code, aguarde alguns segundos — ele aparece aqui mesmo."
                  : "Gere o QR Code e escaneie com o WhatsApp do posto para ativar o envio de mensagens (OTP, Saudade, Pesquisa, Campanhas)."}
              </p>
              <button onClick={conectar} disabled={connecting || awaiting} data-testid="wa-conectar"
                      className="btn-action btn-salvar">
                {(connecting || awaiting) ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />}
                {connecting ? "Gerando..." : awaiting ? "Gerando QR Code..." : "Gerar QR Code"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Teste de envio */}
      <div className="card-soft rounded-2xl p-5" data-testid="wa-teste-card">
        <div className="flex items-center gap-2 mb-1">
          <Smartphone size={18} className="text-[color:var(--brand-blue)]" />
          <div className="font-serif-display text-lg">Testar envio</div>
        </div>
        <p className="text-[12px] text-neutral-500 mb-3">Envie uma mensagem de teste para confirmar que a conexão está entregando.</p>
        {oficialAtivo && (
          <p className="text-[11px] text-neutral-500 -mt-2 mb-3" data-testid="wa-teste-aviso-oficial">
            Pela API oficial: se o telefone não falou com o posto nas últimas 24 h, sai o template aprovado de confirmação de cadastro.
            {oficial?.ambiente === "teste" ? " Ambiente de teste: só envia para os telefones liberados." : ""}
          </p>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Telefone (com DDD)</label>
            <input data-testid="wa-teste-tel" value={tel} onChange={(e) => setTel(maskTel(e.target.value))}
                   inputMode="numeric" maxLength={16} placeholder="(51) 99999-9999" className={inputCls + " mt-1"} />
          </div>
          <button onClick={testar} disabled={sending} data-testid="wa-teste-enviar"
                  className="btn-primary-blue h-10 px-5 text-[12px] font-medium flex items-center gap-1.5 shrink-0">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Enviando..." : "Enviar teste"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, accent }) {
  const bg = accent === "emerald" ? "bg-emerald-50" : accent === "rose" ? "bg-rose-50" : "bg-neutral-50";
  return (
    <div className={`rounded-lg p-2.5 ${bg}`}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-serif-display text-base mt-0.5 leading-tight">{value}</div>
    </div>
  );
}

function ApiOficial({ oficial, erro, carregando, onRecarregar, onAtualizado, onProvedorAlterado }) {
  const [salvando, setSalvando] = useState(null); // "provedor" | "teto" | id do gatilho
  const [testando, setTestando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [teto, setTeto] = useState("");
  const [templates, setTemplates] = useState(null);
  const [verTemplates, setVerTemplates] = useState(false);

  const tetoServidor = oficial ? oficial.teto_24h : null;
  useEffect(() => { if (tetoServidor != null) setTeto(String(tetoServidor)); }, [tetoServidor]);

  const carregarTemplates = useCallback(async () => {
    try {
      const { data } = await masterApi.get("/master/whatsapp/oficial/templates");
      setTemplates(data.templates || []);
    } catch (e) { setTemplates([]); modal.error(apiError(e)); }
  }, []);

  if (!oficial) {
    return (
      <div className="card-soft rounded-[10px] p-5" data-testid="wa-oficial-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={18} className="text-[color:var(--brand-blue)]" />
          <div className="font-serif-display text-lg">API oficial do WhatsApp (Meta)</div>
        </div>
        {erro ? (
          <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
            <div className="text-[12px] text-rose-700 flex items-start gap-1.5">
              <AlertCircle size={14} className="shrink-0 mt-0.5" /> Não foi possível carregar o status da API oficial: {erro}
            </div>
            <button onClick={onRecarregar} disabled={carregando} data-testid="wa-oficial-retry"
                    className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5">
              <RefreshCw size={13} className={carregando ? "animate-spin" : ""} /> Tentar de novo
            </button>
          </div>
        ) : (
          <p className="text-[12px] text-neutral-500 mt-1">Carregando...</p>
        )}
      </div>
    );
  }

  const oficialAtivo = oficial.provedor_ativo === PROVEDOR_OFICIAL;
  const provedores = oficial.provedores_disponiveis || [];
  const gatilhos = oficial.gatilhos || [];
  const allowlist = oficial.allowlist || [];
  const conf = oficial.oficial || {};
  const sessao = conf.sessao || null;
  const configurado = !!conf.configurado;
  const ambiente = oficial.ambiente;
  const custoMsg = (gatilhos.find((g) => g.custo) || {}).custo || "R$ 0,035";
  const tetoMax = Number(oficial.teto_max) || 0;
  const tetoAtual = Number(oficial.teto_24h) || 0;
  const enviados = Number(oficial.enviados_24h) || 0;
  const pct = tetoAtual > 0 ? Math.min(100, Math.round((enviados / tetoAtual) * 100)) : (enviados > 0 ? 100 : 0);
  const tetoNum = Number(teto);
  const tetoInvalido = teto === "" || !Number.isInteger(tetoNum) || tetoNum < 0 || tetoNum > tetoMax;
  const q = qualidadeInfo(sessao?.qualidade);
  const sessaoOk = /^(conectad|connected|ativ|online|open)/i.test(sessao?.status || "");
  const provedorForaDaLista = !provedores.some((p) => p.id === oficial.provedor_ativo);
  const avisoAmbienteTeste = "Ambiente de teste: só envia para os telefones liberados.";

  // PUT /oficial/config devolve o mesmo JSON do status; a tela troca o estado inteiro.
  const aplicarConfig = async (body, msgOk, chave) => {
    setSalvando(chave);
    try {
      const { data } = await masterApi.put("/master/whatsapp/oficial/config", body);
      onAtualizado(data);
      modal.success(msgOk);
      return data;
    } catch (e) { modal.error(apiError(e)); return null; }
    finally { setSalvando(null); }
  };

  const trocarProvedor = async (p) => {
    if (p.id === oficial.provedor_ativo || !p.disponivel || salvando) return;
    const paraOficial = p.id === PROVEDOR_OFICIAL;
    const ok = await modal.confirm(paraOficial ? {
      title: "Usar a API oficial do WhatsApp",
      message: "As mensagens do sistema passam a sair pelo número oficial da Meta, ligado pela plataforma CdxZap.",
      details: [
        { label: "Custo", value: `${custoMsg} por mensagem` },
        "Cada mensagem enviada pela API oficial é cobrada pela Meta.",
        "Campanhas, agradecimento e cupom de saudade (marketing) ficam bloqueados até existir o aceite do cliente.",
        "QR Code, conectar, desconectar e limpar do WhatsApp não oficial ficam desativados. A sessão antiga não é apagada.",
        ...(ambiente === "teste" ? [avisoAmbienteTeste] : []),
      ],
      confirmText: "Usar API oficial",
      cancelText: "Cancelar",
    } : {
      title: "Voltar ao WhatsApp não oficial",
      message: `As mensagens do sistema voltam a sair por: ${p.label}.`,
      details: [
        "As mensagens automáticas da API oficial deixam de sair enquanto este provedor estiver em uso.",
        "Confira abaixo se o WhatsApp não oficial está conectado.",
      ],
      confirmText: "Trocar provedor",
      cancelText: "Cancelar",
    });
    if (!ok) return;
    const data = await aplicarConfig({ provedor: p.id }, `Provedor alterado: ${p.label}`, "provedor");
    if (data && onProvedorAlterado) onProvedorAlterado();
  };

  const motivoBloqueioGatilho = (g) => {
    if (g.ligado) return null; // desligar é sempre permitido
    if (!oficialAtivo) return "Escolha a API oficial como provedor para ligar.";
    if (g.template_status !== "APPROVED") return "Só liga com o template aprovado pela Meta.";
    if (g.categoria && String(g.categoria).toUpperCase() !== "UTILITY") {
      return `A Meta classificou este template como ${g.categoria}. Só mensagens de utilidade podem ser ligadas.`;
    }
    return null;
  };

  const alternarGatilho = async (g, ligar) => {
    if (salvando) return;
    if (ligar) {
      const ok = await modal.confirm({
        title: "Ligar mensagem automática",
        message: g.label,
        details: [
          { label: "Template", value: g.template },
          { label: "Custo", value: `${g.custo || custoMsg} por mensagem` },
          { label: "Limite em 24 h", value: String(tetoAtual) },
          "Cada mensagem enviada é cobrada pela Meta.",
          ...(ambiente === "teste" ? [avisoAmbienteTeste] : []),
        ],
        confirmText: "Ligar",
        cancelText: "Cancelar",
      });
      if (!ok) return;
    }
    await aplicarConfig({ gatilhos: { [g.id]: ligar } },
      ligar ? `Mensagem automática ligada: ${g.label}` : `Mensagem automática desligada: ${g.label}`, g.id);
  };

  const salvarTeto = async () => {
    if (tetoInvalido) { modal.error(`Informe um número inteiro de 0 a ${tetoMax}.`); return; }
    await aplicarConfig({ teto_24h: tetoNum }, `Limite de envios em 24 h: ${tetoNum}`, "teto");
  };

  const testarConexao = async () => {
    setTestando(true);
    try {
      const { data } = await masterApi.post("/master/whatsapp/oficial/testar-conexao");
      if (data.ok) {
        const s = data.sessao || {};
        modal.success({
          title: "Conexão com a API oficial OK",
          details: [
            { label: "Número", value: s.numero || "—" },
            { label: "Nome", value: s.nome || "—" },
            { label: "Status", value: s.status || "—" },
            { label: "Qualidade", value: qualidadeInfo(s.qualidade).label },
          ],
        });
      } else {
        modal.error(data.erro || "Falha ao testar a conexão com a API oficial.");
      }
    } catch (e) { modal.error(apiError(e)); }
    finally { setTestando(false); onRecarregar(); }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const { data } = await masterApi.post("/master/whatsapp/oficial/templates/sincronizar");
      if (data.ok) {
        const lista = data.templates || [];
        setTemplates(lista);
        modal.success({
          title: "Templates atualizados",
          message: `${lista.length} template(s) lido(s) da Meta.`,
          details: lista.map((t) => ({ label: t.nome, value: tplStatusLabel(t.status) })),
        });
      } else {
        modal.error(data.erro || "Falha ao atualizar os templates.");
      }
    } catch (e) { modal.error(apiError(e)); }
    finally { setSincronizando(false); onRecarregar(); }
  };

  const alternarListaTemplates = () => {
    const abrir = !verTemplates;
    setVerTemplates(abrir);
    if (abrir && templates === null) carregarTemplates();
  };

  const temReprovado = (templates || []).some((t) => t.status === "REJECTED");

  return (
    <div className="card-soft rounded-[10px] p-5 space-y-4" data-testid="wa-oficial-card">
      {/* Cabeçalho */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={18} className="text-[color:var(--brand-blue)]" />
            <div className="font-serif-display text-lg">API oficial do WhatsApp (Meta)</div>
          </div>
          <p className="text-[12px] text-neutral-500">
            Número oficial da Meta ligado pela plataforma CdxZap. A chave de acesso fica só no servidor.
          </p>
        </div>
        <button onClick={onRecarregar} disabled={carregando} data-testid="wa-oficial-reload"
                className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5">
          <RefreshCw size={13} className={carregando ? "animate-spin" : ""} /> Atualizar
        </button>
      </div>

      {/* Ambiente + telefones liberados */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="wa-oficial-ambiente">
        <span className={`chip text-[10px] py-0.5 uppercase tracking-wider ${
          ambiente === "producao" ? "chip-green" : ambiente === "teste" ? "chip-yellow" : "chip-red"}`}>
          Ambiente: {AMBIENTE_LABEL[ambiente] || "não definido"}
        </span>
        {allowlist.length > 0 && (
          <>
            <span className="text-[11px] text-neutral-500 ml-1">Telefones liberados:</span>
            {allowlist.map((t) => (
              <span key={t} className="chip chip-gray text-[10px] py-0.5 font-mono-ibm">{t}</span>
            ))}
          </>
        )}
      </div>
      {ambiente === "teste" && (
        <div className="rounded-lg p-3 flex items-start gap-2 bg-amber-50 border border-amber-200" data-testid="wa-oficial-aviso-teste">
          <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={16} />
          <div className="text-[12px] text-amber-900 leading-relaxed">
            <b>{avisoAmbienteTeste}</b>
            {allowlist.length === 0 && <> Nenhum telefone liberado no servidor.</>}
          </div>
        </div>
      )}
      {!ambiente && (
        <div className="rounded-lg p-3 flex items-start gap-2 bg-rose-50 border border-rose-200" data-testid="wa-oficial-aviso-ambiente">
          <AlertCircle className="text-rose-600 shrink-0 mt-0.5" size={16} />
          <div className="text-[12px] text-rose-900 leading-relaxed">
            <b>Ambiente do servidor não definido.</b> Por segurança, a API oficial não envia nenhuma mensagem até a CDX ajustar o servidor.
          </div>
        </div>
      )}

      {/* Provedor */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Provedor de envio</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {provedores.map((p) => {
            const ativo = p.id === oficial.provedor_ativo;
            const bloqueado = !p.disponivel && !ativo;
            return (
              <button key={p.id} type="button" onClick={() => trocarProvedor(p)}
                      disabled={bloqueado || ativo || !!salvando}
                      data-testid={`wa-provedor-${p.id}`}
                      className={`text-left rounded-lg border p-3 transition-colors ${
                        ativo ? "border-[color:var(--brand-blue)] bg-blue-50/60 cursor-default"
                              : "border-neutral-200 bg-white hover:bg-neutral-50"
                      } ${bloqueado ? "opacity-60 cursor-not-allowed" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-[13px] font-medium text-neutral-800 min-w-0">
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      ativo ? "border-[color:var(--brand-blue)]" : "border-neutral-300"}`}>
                      {ativo && <span className="w-2 h-2 rounded-full bg-[color:var(--brand-blue)]" />}
                    </span>
                    <span className="truncate">{p.label}</span>
                  </span>
                  {salvando === "provedor" && !ativo ? <Loader2 size={14} className="animate-spin text-neutral-400 shrink-0" />
                    : ativo ? <span className="chip chip-green text-[10px] py-0.5 uppercase tracking-wider shrink-0">Em uso</span> : null}
                </div>
                {!p.disponivel && (
                  <div className="text-[11px] text-rose-700 mt-1">Indisponível: {p.motivo || "não configurado no servidor"}</div>
                )}
                {ativo && (
                  <div className="text-[11px] text-neutral-500 mt-1">
                    {oficial.provedor_config ? "Escolhido nesta tela" : "Padrão do servidor"}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {provedorForaDaLista && (
          <p className="text-[11px] text-neutral-500 mt-1.5">
            Em uso agora: <b>{oficial.provedor_ativo === "mock" ? "Simulação (nenhuma mensagem sai)" : oficial.provedor_ativo}</b>
          </p>
        )}
      </div>

      {/* Número oficial (sessão no CdxZap) */}
      <div className={`rounded-lg border border-neutral-200 border-l-4 p-3 ${
        configurado && sessaoOk ? "border-l-emerald-500" : "border-l-rose-500"}`} data-testid="wa-oficial-sessao">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[13px] font-medium text-neutral-800">Número oficial</div>
          <button onClick={testarConexao} disabled={testando || !configurado} data-testid="wa-oficial-testar"
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-50">
            {testando ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />}
            {testando ? "Testando..." : "Testar conexão"}
          </button>
        </div>
        {!configurado ? (
          <p className="text-[12px] text-neutral-500 mt-2">
            A chave do CdxZap ainda não foi configurada no servidor. Sem ela a API oficial fica indisponível.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
            <Mini label="Número" value={sessao?.numero || "—"} />
            <Mini label="Nome" value={sessao?.nome || "—"} />
            <Mini label="Status" value={sessao?.status || "—"} accent={sessao ? (sessaoOk ? "emerald" : "rose") : null} />
            <Mini label="Qualidade" value={q.label} accent={q.accent} />
            <Mini label="Último teste" value={fmtDataHora(conf.ultimo_teste_em) || "—"} />
          </div>
        )}
        {conf.erro && (
          <div className="text-[12px] text-rose-700 mt-2 flex items-start gap-1.5" data-testid="wa-oficial-erro">
            <AlertCircle size={14} className="shrink-0 mt-0.5" /> <span className="break-words min-w-0">{conf.erro}</span>
          </div>
        )}
      </div>

      {/* Mensagens automáticas */}
      <div data-testid="wa-oficial-gatilhos">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="font-serif-display text-base">Mensagens automáticas</div>
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-[11px] text-neutral-500">
              Templates atualizados em: <b>{fmtDataHora(oficial.templates_atualizados_em) || "nunca"}</b>
            </span>
            <button onClick={sincronizar} disabled={sincronizando || !configurado} data-testid="wa-oficial-sincronizar"
                    className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw size={13} className={sincronizando ? "animate-spin" : ""} />
              {sincronizando ? "Atualizando..." : "Atualizar templates"}
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-neutral-200 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Mensagem</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Template</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Custo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Ligado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {gatilhos.map((g) => {
                const motivo = motivoBloqueioGatilho(g);
                // o aviso de "escolha a API oficial" aparece uma vez só, abaixo da tabela
                const motivoLinha = motivo && oficialAtivo ? motivo : null;
                return (
                  <TableRow key={g.id} data-testid={`wa-gatilho-${g.id}`}>
                    <TableCell className="text-[13px] font-medium min-w-[220px]">
                      {g.label}
                      {motivoLinha && <div className="text-[11px] font-normal text-neutral-500 mt-0.5">{motivoLinha}</div>}
                    </TableCell>
                    <TableCell className="text-xs font-mono-ibm whitespace-nowrap">{g.template || "—"}</TableCell>
                    <TableCell><TplPill status={g.template_status} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {g.custo || "—"} <span className="text-neutral-400">/ msg</span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2" title={motivo || undefined}>
                        <Switch data-testid={`wa-gatilho-switch-${g.id}`} checked={!!g.ligado}
                                disabled={!!motivo || !!salvando}
                                onCheckedChange={(v) => alternarGatilho(g, v)} />
                        {salvando === g.id && <Loader2 size={13} className="animate-spin text-neutral-400" />}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              {gatilhos.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-6">Nenhuma mensagem automática disponível.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {!oficialAtivo && (
          <p className="text-[11px] text-neutral-500 mt-2">As mensagens automáticas só saem com a API oficial como provedor em uso.</p>
        )}
        {!oficial.marketing_liberado && (
          <div className="rounded-lg p-3 mt-2 flex items-start gap-2 bg-neutral-50 border border-neutral-200" data-testid="wa-oficial-aviso-marketing">
            <AlertCircle className="text-neutral-500 shrink-0 mt-0.5" size={15} />
            <div className="text-[12px] text-neutral-700 leading-relaxed">
              Campanhas e cupom de saudade (promoções) só saem pela API oficial depois da liberação em Mensagens automáticas &gt; Envio das mensagens e com o template aprovado pela Meta; o agradecimento não sai pela API oficial.
            </div>
          </div>
        )}

        <button type="button" onClick={alternarListaTemplates} data-testid="wa-oficial-ver-templates"
                className="mt-3 text-[12px] font-medium text-[color:var(--brand-blue)] flex items-center gap-1">
          <ChevronDown size={13} className={`transition-transform ${verTemplates ? "rotate-180" : ""}`} />
          {verTemplates ? "Ocultar templates cadastrados" : "Ver todos os templates cadastrados na Meta"}
        </button>
        {verTemplates && (
          <div className="mt-2 space-y-2" data-testid="wa-oficial-templates">
            {templates === null ? (
              <p className="text-[12px] text-neutral-500">Carregando...</p>
            ) : (
              <div className="rounded-lg border border-neutral-200 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-neutral-50">
                      <TableHead className="text-[10px] uppercase tracking-widest">Template</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest">Categoria</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest">Motivo da reprovação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((t) => (
                      <TableRow key={t.nome}>
                        <TableCell className="text-xs font-mono-ibm whitespace-nowrap">{t.nome}</TableCell>
                        <TableCell className="text-xs">{t.categoria || "—"}</TableCell>
                        <TableCell><TplPill status={t.status} /></TableCell>
                        <TableCell className="text-xs text-neutral-500 max-w-[280px] truncate" title={t.motivo_rejeicao || ""}>
                          {t.motivo_rejeicao || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {templates.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-neutral-500 py-6">Nenhum template salvo. Clique em Atualizar templates.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            {temReprovado && (
              <p className="text-[11px] text-neutral-500">
                Template reprovado é corrigido no painel administrativo do CdxZap. Depois, clique em Atualizar templates.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Limite de envios em 24 h */}
      <div className="grid md:grid-cols-2 gap-3 items-end" data-testid="wa-oficial-teto-bloco">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">
            Limite de envios em 24 h <span className="normal-case text-neutral-400">(0 a {tetoMax})</span>
          </label>
          <div className="flex gap-2 mt-1">
            <input data-testid="wa-oficial-teto" type="number" inputMode="numeric" min={0} max={tetoMax} step={1}
                   value={teto} onChange={(e) => setTeto(e.target.value.replace(/[^\d]/g, ""))}
                   className={inputCls + (tetoInvalido ? " border-rose-400" : "")} />
            <button onClick={salvarTeto} disabled={salvando === "teto" || tetoInvalido || tetoNum === tetoAtual}
                    data-testid="wa-oficial-teto-salvar"
                    className="btn-primary-blue h-10 px-4 text-[12px] font-medium shrink-0 flex items-center gap-1.5 disabled:opacity-50">
              {salvando === "teto" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {salvando === "teto" ? "Salvando..." : "Salvar limite"}
            </button>
          </div>
        </div>
        <div>
          <div className="flex items-baseline justify-between gap-2 text-[11px] text-neutral-600 mb-1">
            <span>Enviados nas últimas 24 h</span>
            <b className="number-tabular" data-testid="wa-oficial-enviados">{enviados} / {tetoAtual}</b>
          </div>
          <div className="h-2 rounded-sm bg-neutral-100 overflow-hidden">
            <div className={`h-full ${pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                 style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[11px] text-neutral-500 mt-1">
            {tetoAtual === 0
              ? "Com limite 0 nenhuma mensagem automática sai."
              : "Atingido o limite, os envios automáticos param até a contagem das últimas 24 h baixar."}
          </div>
        </div>
      </div>
    </div>
  );
}

function Historico() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [tipo, setTipo] = useState("");
  const [sucesso, setSucesso] = useState("");

  const load = async (targetPage = 1) => {
    const params = new URLSearchParams({ page: String(targetPage), page_size: "30" });
    if (tipo) params.set("tipo", tipo);
    if (sucesso === "ok") params.set("sucesso", "true");
    if (sucesso === "fail") params.set("sucesso", "false");
    try {
      const { data } = await masterApi.get(`/master/whatsapp/historico?${params.toString()}`);
      setItems(data.items || []);
      setPage(data.page || targetPage);
      setTotalPages(data.total_pages || 0);
      setTotal(data.total || 0);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(1); }, [tipo, sucesso]);

  return (
    <div className="space-y-3" data-testid="wa-historico-section">
      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="hist-tipo"
                  className={inputCls + " mt-1 w-44"}>
            <option value="">Todos</option>
            {Object.entries(TIPOS_LABEL).map(([k, lbl]) => (<option key={k} value={k}>{lbl}</option>))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Status</label>
          <select value={sucesso} onChange={(e) => setSucesso(e.target.value)} data-testid="hist-sucesso"
                  className={inputCls + " mt-1 w-36"}>
            <option value="">Todos</option>
            <option value="ok">Sucesso</option>
            <option value="fail">Falha</option>
          </select>
        </div>
        <div className="ml-auto text-[11px] text-neutral-500">{total} envios</div>
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Quando</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Tent.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Detalhe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((m, idx) => (
              <TableRow key={idx} data-testid={`hist-row-${idx}`}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(m.criado_em).toLocaleString("pt-BR")}</TableCell>
                <TableCell className="text-xs">{TIPOS_LABEL[m.tipo] || m.tipo}</TableCell>
                <TableCell className="text-xs">{m.telefone}</TableCell>
                <TableCell>
                  {m.sucesso
                    ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(16,185,129,0.18)", color: "#047857" }}>OK</span>
                    : <span className="chip text-[10px] py-0.5" style={{ background: "rgba(244,63,94,0.18)", color: "#9F1239" }}>Falha</span>}
                </TableCell>
                <TableCell className="text-xs">{m.tentativas || 1}</TableCell>
                <TableCell className="text-xs text-neutral-500 max-w-[280px] truncate" title={m.detalhes}>{m.detalhes || "—"}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-6">Nenhum envio.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <button data-testid="hist-prev" disabled={page <= 1} onClick={() => load(page - 1)}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
            <ChevronLeft size={13} /> Anterior
          </button>
          <span className="text-[11px] text-neutral-600">Página <b>{page}</b> de <b>{totalPages}</b></span>
          <button data-testid="hist-next" disabled={page >= totalPages} onClick={() => load(page + 1)}
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
            Próxima <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function EnvioUnico() {
  const [tipo, setTipo] = useState("sumidos");
  const [dias, setDias] = useState(30);
  const [items, setItems] = useState([]);

  const load = async () => {
    const params = new URLSearchParams({ tipo });
    if (tipo === "sumidos") params.set("dias", String(dias));
    try {
      const { data } = await masterApi.get(`/master/clientes-insights?${params.toString()}`);
      setItems(data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [tipo, dias]);

  const enviar = async (cliente_id) => {
    try {
      const { data } = await masterApi.post("/master/whatsapp/saudade", { cliente_id });
      if (data.ok && data.estado === "incerto") modal.success("Cupom enviado, sem confirmação do provedor. Não reenvie: o cliente pode usar no próximo abastecimento.");
      else if (data.ok) modal.success("Cupom enviado · cliente pode usar no próximo abastecimento");
      else modal.error({ message: "Falha no envio. Use o link manual ou marque como enviado.", details: detalhesDe(data.detalhe) });
      load();
      return data.link_manual;
    } catch (e) { modal.error(apiError(e)); }
  };

  const copiarLink = async (cliente_id) => {
    const link = await enviar(cliente_id);
    if (link) {
      try { await navigator.clipboard.writeText(link); modal.success("Link copiado"); }
      catch { modal.info(link); }
    }
  };

  const marcarEnviado = async (cliente_id) => {
    try {
      await masterApi.post("/master/whatsapp/marcar-enviado", { cliente_id });
      modal.success("Marcado como enviado");
      load();
    } catch (e) { modal.error(apiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="card-soft rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Segmento</label>
          <select data-testid="wa-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputCls + " mt-1 w-56"}>
            <option value="sumidos">Clientes Sumidos</option>
            <option value="primeiro_nao_voltou">1º Cadastro sem retorno</option>
            <option value="saudade_enviado">Saudade já enviado</option>
          </select>
        </div>
        {tipo === "sumidos" && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Dias</label>
            <input data-testid="wa-dias" type="number" value={dias} onChange={(e) => setDias(parseInt(e.target.value) || 30)}
                   className={inputCls + " mt-1 w-24"} />
          </div>
        )}
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Nome</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Telefone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Placa</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Último</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Abast.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Saudade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`wa-row-${c.id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell>{formatPhone(c.telefone)}</TableCell>
                <TableCell>{c.placa}</TableCell>
                <TableCell className="text-xs">{c.ultimo_abastecimento_em ? new Date(c.ultimo_abastecimento_em).toLocaleDateString("pt-BR") : "—"}</TableCell>
                <TableCell>{c.total_abastecimentos || 0}</TableCell>
                <TableCell>{c.cupom_saudade_ativo ? <Check size={15} className="inline text-emerald-600" /> : "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button data-testid={`wa-send-${c.id}`} onClick={() => enviar(c.id)}
                            className="btn-primary-blue h-8 px-3 text-[11px] font-medium flex items-center gap-1">
                      <Send size={11} /> Enviar
                    </button>
                    <button data-testid={`wa-link-${c.id}`} onClick={() => copiarLink(c.id)}
                            title="Copiar link" className="icon-action">
                      <Link2 size={14} />
                    </button>
                    <button data-testid={`wa-mark-${c.id}`} onClick={() => marcarEnviado(c.id)}
                            title="Marcar enviado" className="icon-action icon-action--green">
                      <CheckCircle2 size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-6">Nenhum cliente no segmento.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
